/**
 * DailyRitual — pinned checklist of "is the daily clock on track"
 *
 * Synthesizes existing store endpoints into a single ordered list: 1am
 * cutoff, EOD graders, calibration ritual, 8am picker, scrape health,
 * morning brief, anomaly state. Each item is a tri-state pill —
 * done / pending / late — derived from data we already poll, so adding
 * the panel doesn't trigger any new fetches.
 *
 * Lives at the top of the left rail; uses the same .panel chrome.
 */
import { html, type TemplateResult } from "lit-html";
import { store } from "../data/store";
import type {
  MacStatus, VpsStatus, AnomalyBadges, MorningBriefLatest, ActivityEvent,
} from "../data/types";

type RitualState = "done" | "pending" | "late" | "skip";
interface RitualItem {
  label:    string;
  due:      string;       // human-readable due time, e.g. "1:00 AM ET"
  state:    RitualState;
  detail:   string;       // short status line under the label
  manual?:  boolean;      // marked when there's no automated signal
}

export function renderDailyRitualPanel(): TemplateResult {
  const items = computeItems();
  const done    = items.filter((i) => i.state === "done").length;
  const lateCount = items.filter((i) => i.state === "late").length;
  const total   = items.length;
  const headerCls = lateCount > 0 ? "is-late" : done === total ? "is-done" : "is-progress";

  return html`
    <section class="panel ritual ${headerCls}">
      <div class="panel__title">
        <span>Daily ritual</span>
        <span class="panel__title-actions ritual__count">
          ${done}/${total} ${lateCount > 0 ? `· ${lateCount} late` : ""}
        </span>
      </div>
      <ul class="ritual__list">
        ${items.map(renderRow)}
      </ul>
    </section>
  `;
}

function renderRow(it: RitualItem): TemplateResult {
  const dotCls = it.state === "done"    ? "is-ok"
              : it.state === "late"    ? "is-bad"
              : it.state === "skip"    ? "is-idle"
              : "is-warn";
  return html`
    <li class="ritual__row ritual__row--${it.state}">
      <span class="status-dot ${dotCls}"></span>
      <div class="ritual__body">
        <div class="ritual__label">
          <span>${it.label}</span>
          ${it.manual ? html`<span class="ritual__manual" title="manual step">m</span>` : ""}
        </div>
        <div class="ritual__detail">${it.detail}</div>
      </div>
      <div class="ritual__due">${it.due}</div>
    </li>
  `;
}

// =============================================================
// Item derivation — pure function over store snapshots
// =============================================================
function computeItems(): RitualItem[] {
  const macE  = store.get<MacStatus>("macStatus");
  const vpsE  = store.get<VpsStatus & { _stale?: boolean }>("vpsStatus");
  const anomE = store.get<AnomalyBadges>("anomalies");
  const briefE = store.get<MorningBriefLatest>("morningBrief");
  const actE = store.get<ActivityEvent[]>("activity");

  const today = todayInETString();
  const items: RitualItem[] = [];

  // 1) Slate cutoff at 1am ET — implicitly handled by sync_dashboard.py
  items.push({
    label:  "Slate cutover (1am ET)",
    due:    "01:00 ET",
    state:  pastEt(1, 0) ? "done" : "pending",
    detail: pastEt(1, 0)
      ? "yesterday's bets archived; today's slate is active"
      : "waiting for tonight's rollover",
  });

  // 2) NBA EOD grader (~1:00 AM)
  items.push(makeAgentItem(macE.data, "com.yurysankov.builder-grading-nba",
    "NBA EOD grader", "01:00 ET", today));
  // 3) MLB EOD grader (~1:30 AM)
  items.push(makeAgentItem(macE.data, "com.yurysankov.builder-grading-mlb",
    "MLB EOD grader", "01:30 ET", today));
  // 4) NHL EOD grader (~2:30 AM)
  items.push(makeAgentItem(macE.data, "com.yurysankov.builder-grading-nhl",
    "NHL EOD grader", "02:30 ET", today));

  // 5) Manual calibration review — no automated signal yet, mark "manual"
  items.push({
    label:  "Calibration review",
    due:    "after EOD",
    state:  "skip",
    detail: "run ./notebooks/run_calibration.sh; edit worker.js prompts",
    manual: true,
  });

  // 6) 8am ET picker fire (NBA/MLB/NHL unified daily picker)
  items.push(makeAgentItem(macE.data, "com.yurysankov.nba-ai-picker",
    "8am picker", "08:00 ET", today));

  // 6b) WNBA picker fires later — 4pm ET (inactives drop mid-afternoon).
  // Only show as "late" once past 4pm; before that it's simply pending.
  items.push(makeAgentItem(macE.data, "com.yurysankov.wnba-ai-picker",
    "WNBA picker (4pm ET)", "16:00 ET", today));

  // 7) VPS scrape healthy — last_scrape_at within 15 min
  const v = vpsE.data;
  const lastScrape = v?.last_scrape_at ? Date.parse(v.last_scrape_at) : 0;
  const scrapeAge = lastScrape ? (Date.now() - lastScrape) / 60_000 : -1;
  items.push({
    label:  "Scrape pulse",
    due:    "every 5m",
    state:  scrapeAge < 0       ? "late"
          : scrapeAge <= 15     ? "done"
          : "late",
    detail: scrapeAge < 0
      ? "no scrape data — VPS reporter offline"
      : `last scrape ${Math.round(scrapeAge)}m ago · ${v?.cadence ?? "unknown"}`,
  });

  // 8) Morning brief generated — briefE.data.date === today
  const briefDate = briefE.data?.date ?? null;
  items.push({
    label:  "Morning brief",
    due:    "08:00 ET",
    state:  briefDate === today ? "done"
          : pastEt(8, 30)        ? "late"
          : "pending",
    detail: briefDate
      ? `latest: ${briefDate}`
      : "no brief generated yet",
  });

  // 9) Anomalies clear
  const badges = anomE.data?.badges ?? [];
  const fired  = badges.filter((b) => b.severity === "error" || b.severity === "warn");
  items.push({
    label:  "Anomalies",
    due:    "live",
    state:  fired.length === 0 ? "done"
          : fired.some((b) => b.severity === "error") ? "late"
          : "pending",
    detail: fired.length === 0
      ? "no fires"
      : `${fired.length} firing — ` + fired.slice(0, 2).map((b) => b.title).join(", "),
  });

  // 10) Recent commits today — proxy for "I shipped something"
  const commitsToday = (macE.data?.recent_commits ?? []).filter((c) => {
    const d = c.iso_date ? toEtDateString(c.iso_date) : "";
    return d === today;
  });
  items.push({
    label:  "Ship something",
    due:    "—",
    state:  commitsToday.length > 0 ? "done" : "skip",
    detail: commitsToday.length > 0
      ? `${commitsToday.length} commit${commitsToday.length === 1 ? "" : "s"} today`
      : "no commits yet today",
    manual: true,
  });

  // Quietly use actE so the panel re-renders when activity ticks (it
  // doesn't change individual rows but keeps freshness consistent).
  void actE;

  return items;
}

// =============================================================
// Helpers — ET-aware date math without a tz library
// =============================================================
function todayInETString(): string {
  return toEtDateString(new Date().toISOString());
}

function toEtDateString(iso: string): string {
  const d = new Date(iso);
  // en-CA gives YYYY-MM-DD; America/New_York handles DST automatically.
  return d.toLocaleDateString("en-CA", { timeZone: "America/New_York" });
}

function pastEt(hour: number, minute: number): boolean {
  // Compare current ET wall clock against the given hour:minute today.
  const parts = new Date().toLocaleString("en-US", {
    timeZone:    "America/New_York",
    hour:        "2-digit",
    minute:      "2-digit",
    hour12:      false,
  });
  // parts looks like "07:42"
  const [hStr, mStr] = parts.split(":");
  const h = parseInt(hStr ?? "0", 10);
  const m = parseInt(mStr ?? "0", 10);
  return h > hour || (h === hour && m >= minute);
}

function makeAgentItem(
  mac: MacStatus | null,
  label: string,
  display: string,
  due: string,
  today: string,
): RitualItem {
  const a = mac?.launchagents.find((x) => x.label === label) ?? null;
  if (!a) {
    return { label: display, due, state: "skip",
             detail: "agent not in mac-status (may have been renamed)" };
  }
  const lastFireDate = a.last_fire ? toEtDateString(a.last_fire) : null;
  const ranToday = lastFireDate === today;

  // Pull schedule_label apart heuristically — "8:00 AM ET" — to decide if "late"
  let dueHour = 0, dueMin = 0;
  if (due.includes(":")) {
    const [hh, mm] = due.split(" ")[0]!.split(":");
    dueHour = parseInt(hh ?? "0", 10);
    dueMin  = parseInt(mm ?? "0", 10);
  }
  const past = pastEt(dueHour, dueMin);
  const state: RitualState =
    ranToday   ? "done"
  : past       ? (a.last_exit_code === 0 ? "late" : "late")
  : "pending";

  const detail =
    ranToday && a.last_fire ? `ran ${formatRelMinutes(a.last_fire)} ago`
  : a.last_fire             ? `last fire ${toEtDateString(a.last_fire)} (not today)`
  : "no fire recorded";

  return { label: display, due, state, detail };
}

function formatRelMinutes(iso: string): string {
  const ms = Date.now() - Date.parse(iso);
  const m = Math.floor(ms / 60_000);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m`;
}
